interface AsaasConfig {
  apiKey: string;
  baseURL: string;
  environment: 'sandbox' | 'production';
}

interface Cliente {
  name: string;
  email: string;
  cpfCnpj: string;
  phone: string;
  postalCode: string;
  address: string;
  addressNumber: string;
  complement?: string;
  province: string;
  city: string;
  state: string;
}

interface Plano {
  id: string;
  nome: string;
  preco: number;
  descricao: string;
  funcionalidades: string[];
}

interface Cobranca {
  customer: string;
  billingType: 'PIX' | 'CREDIT_CARD' | 'BOLETO';
  value: number;
  dueDate: string;
  description: string;
  externalReference: string;
}

interface Assinatura {
  customer: string;
  billingType: 'PIX' | 'CREDIT_CARD';
  value: number;
  nextDueDate: string;
  cycle: 'MONTHLY';
  description: string;
  externalReference: string;
}

class AsaasService {
  private config: AsaasConfig;

  constructor() {
    // Detectar ambiente baseado na URL atual
    const currentHost = window.location.hostname;
    const isHomolog = currentHost.includes('cipa-homolog.ehsfacil.com');
    
    this.config = {
      apiKey: isHomolog 
        ? (process.env.REACT_APP_ASAAS_API_KEY_HML || '$aact_hmlg_000MzkwODA2MWY2OGM3MWRlMDU2NWM3MzJlNzZmNGZhZGY6OmRhOWFjYjE3LWMyNWMtNGZhOC1iYjczLTlkNGY2NTU1NmRiMjo6JGFhY2hfMGVlZjQwMGYtOTA1NC00NDBjLWE1NzMtMDAyMTNhYTdmYTY5')
        : (process.env.REACT_APP_ASAAS_API_KEY || '$aact_prod_000MzkwODA2MWY2OGM3MWRlMDU2NWM3MzJlNzZmNGZhZGY6OjAwMzdkNGRmLWU3MDQtNDZhZi1iOTAwLTc4ZjMwNTRlODk2NTo6JGFhY2hfMzIxMDRlNzctMDcyMy00YTE3LWE3YjctODZmODFhMWI2OTlm'),
      baseURL: '/api/asaas-proxy.php', // SEMPRE usar proxy PHP
      environment: isHomolog ? 'sandbox' : 'production'
    };
    
    console.log(`🔧 [ASAAS-SERVICE] Ambiente detectado: ${this.config.environment} (${currentHost})`);
  }

  // Criar cliente no Asaas
  async criarCliente(dadosCliente: Cliente) {
    try {      
      const response = await fetch(`${this.config.baseURL}/v3/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'access_token': this.config.apiKey
        },
        body: JSON.stringify(dadosCliente)
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.errors?.[0]?.description || 'Erro ao criar cliente');
      }

      return result;
    } catch (error) {
      console.error('❌ [ASAAS] Erro ao criar cliente:', error);
      throw error;
    }
  }

  // Criar cobrança única
  async criarCobranca(clienteId: string, plano: Plano, metodoPagamento: 'PIX' | 'CREDIT_CARD') {
    try {

      const cobranca: Cobranca = {
        customer: clienteId,
        billingType: metodoPagamento,
        value: plano.preco,
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        description: `Assinatura ${plano.nome} - CIPA Fácil EHS`,
        externalReference: `plano-${plano.id}-${Date.now()}`
      };

      const response = await fetch(`${this.config.baseURL}/v3/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'access_token': this.config.apiKey
        },
        body: JSON.stringify(cobranca)
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.errors?.[0]?.description || 'Erro ao criar cobrança');
      }

      return result;
    } catch (error) {
      console.error('❌ [ASAAS] Erro ao criar cobrança:', error);
      throw error;
    }
  }

  // Processar pagamento de cartão de crédito imediatamente
  async processarPagamentoCartao(clienteId: string, plano: Plano, dadosCartao: any) {
    try {
      
      const pagamento: any = {
        customer: clienteId,
        billingType: 'CREDIT_CARD',
        value: plano.preco,
        dueDate: new Date().toISOString().split('T')[0], // Hoje
        description: `Pagamento ${plano.nome} - CIPA Fácil EHS`,
        externalReference: `pagamento-${plano.id}-${Date.now()}`,
        creditCard: {
          holderName: dadosCartao.holderName,
          number: dadosCartao.number.replace(/\s/g, ''),
          expiryMonth: dadosCartao.expiryMonth,
          expiryYear: dadosCartao.expiryYear,
          ccv: dadosCartao.cvv
        },
        creditCardHolderInfo: {
          name: dadosCartao.holderName,
          email: dadosCartao.email || '',
          cpfCnpj: dadosCartao.cpfCnpj || '',
          postalCode: dadosCartao.postalCode || '',
          addressNumber: dadosCartao.addressNumber || '',
          phone: dadosCartao.phone || ''
        }
      };

      const response = await fetch(`${this.config.baseURL}/v3/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'access_token': this.config.apiKey
        },
        body: JSON.stringify(pagamento)
      });

      const result = await response.json();
      
      if (!response.ok) {
        // Preservar a estrutura de erros do Asaas
        const error = new Error('Erro ao processar pagamento');
        (error as any).errors = result.errors || [];
        (error as any).response = { data: result };
        throw error;
      }

      return result;
    } catch (error) {
      console.error('❌ [ASAAS] Erro ao processar pagamento:', error);
      throw error;
    }
  }

  // Criar assinatura recorrente
  async criarAssinatura(clienteId: string, plano: Plano, metodoPagamento: 'PIX' | 'CREDIT_CARD') {
    try {
      
      const assinatura: Assinatura = {
        customer: clienteId,
        billingType: metodoPagamento,
        value: plano.preco,
        nextDueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        cycle: 'MONTHLY',
        description: `Assinatura ${plano.nome} - CIPA Fácil EHS`,
        externalReference: `assinatura-${plano.id}-${Date.now()}`
      };
      // Criar AbortController para timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 segundos

      const response = await fetch(`${this.config.baseURL}/v3/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'access_token': this.config.apiKey
        },
        body: JSON.stringify(assinatura),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const result = await response.json();
      
      if (!response.ok) {
        console.error('❌ [ASAAS] Erro na API:', result);
        throw new Error(result.errors?.[0]?.description || 'Erro ao criar assinatura');
      }

      return result;
    } catch (error) {
      console.error('❌ [ASAAS] Erro ao criar assinatura:', error);
      
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Timeout: A requisição demorou mais de 30 segundos para responder');
      }
      
      throw error;
    }
  }

  // Obter QR Code PIX para uma cobrança
  async obterQrCodePix(paymentId: string) {
    try {
      
      const response = await fetch(`${this.config.baseURL}/v3/payments/${paymentId}/pixQrCode`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'access_token': this.config.apiKey
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.errors?.[0]?.description || 'Erro ao obter QR Code PIX');
      }

      return result;
    } catch (error) {
      console.error('❌ [ASAAS] Erro ao obter QR Code PIX:', error);
      throw error;
    }
  }

  // Consultar status de pagamento
  async consultarPagamento(paymentId: string) {
    try {
      const response = await fetch(`${this.config.baseURL}/v3/payments/${paymentId}`, {
        method: 'GET',
        headers: {
          'access_token': this.config.apiKey
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.errors?.[0]?.description || 'Erro ao consultar pagamento');
      }

      return result;
    } catch (error) {
      console.error('❌ [ASAAS] Erro ao consultar pagamento:', error);
      throw error;
    }
  }

  // Consultar status de assinatura
  async consultarAssinatura(subscriptionId: string) {
    try {
      const response = await fetch(`${this.config.baseURL}/v3/subscriptions/${subscriptionId}`, {
        method: 'GET',
        headers: {
          'access_token': this.config.apiKey
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.errors?.[0]?.description || 'Erro ao consultar assinatura');
      }

      return result;
    } catch (error) {
      console.error('❌ [ASAAS] Erro ao consultar assinatura:', error);
      throw error;
    }
  }

  // Obter configuração atual
  getConfig() {
    return this.config;
  }
}

export default new AsaasService();
export type { Cliente, Plano, Cobranca, Assinatura };
